// AllPipelines.jsx — the "All Pipelines" tab.
//
// Section 1 — DEFAULT AUTOMATED PIPELINES: the flows that start AUTOMATICALLY
// the moment a brand is created (idea generation & auto-posting, brand
// enrichment), listed per brand with their cadence (quantity / period, split
// equally over the period) and any end date. The idea/auto-posting cadence is
// editable per brand — saving it updates the brand's settings and the worker
// re-spreads posting times (e.g. 3×/day → early / mid / end of day). A per-brand
// per-flow enable/disable toggle switches a flow off for one brand without
// halting the whole brand. Recurring FlowRuns the brand already has are shown
// alongside.
//
// Section 2 — OTHER (NON-AUTOMATIC) FLOWS: fully-pipelined flows that DON'T run
// on brand creation (email / faq / seo, plus any future registry flow). Each can
// be MADE AUTOMATIC for FUTURE brands via a policy scoped on two axes —
// all companies or one, all brands or one — at a chosen cadence. Existing
// policies are listed and removable.
//
// Super-admin only (the backing endpoints are super-only). Data comes from
// GET /api/admin/flows/pipelines.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { get, post, remove } from "../../api";

// Friendly label for each BrandSettings.autopilot feature a flow auto-publishes
// under — shown next to its inline auto-publish toggle.
const AUTOPILOT_LABEL = {
  post: "auto-post", answer: "auto-FAQ", article: "auto-article",
  community: "auto-community", crm: "auto-CRM", email: "auto-email",
};

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
}
function fmtDateTime(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(); } catch { return "—"; }
}
// <input type=date> wants YYYY-MM-DD.
function toDateInput(d) {
  if (!d) return "";
  try { return new Date(d).toISOString().slice(0, 10); } catch { return ""; }
}

// One-line recurrence/projection summary for a recurrence flow.
function recurrenceProjection(f) {
  const rate = `${f.quantity}/${f.period}` + (f.perDay ? ` · ~${f.perDay}/day` : "");
  if (f.recurring) return `${rate} · next ${fmtDateTime(f.nextRunAt)} · until ${fmtDate(f.endDate)}`;
  return `${rate} · one-shot`;
}

function Toggle({ enabled, busy, onChange }) {
  return (
    <button
      className="ap-toggle"
      disabled={busy}
      onClick={() => onChange(!enabled)}
      title={enabled ? "Disable this flow for this brand" : "Enable this flow for this brand"}
      style={{
        background: enabled ? "#059669" : "#e5e7eb",
        color: enabled ? "#fff" : "#6b7280",
        opacity: busy ? 0.6 : 1,
      }}
    >
      {busy ? "…" : enabled ? "Enabled" : "Disabled"}
    </button>
  );
}

// Inline cadence editor for an editable standing flow (e.g. idea/auto-posting).
// quantity × period, split equally over the period, with an optional end date.
function CadenceEditor({ flow, periods, busy, onSave }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(flow.quantity || 1);
  const [period, setPeriod] = useState(flow.period || "day");
  const [endDate, setEndDate] = useState(toDateInput(flow.endDate));

  useEffect(() => {
    setQty(flow.quantity || 1);
    setPeriod(flow.period || "day");
    setEndDate(toDateInput(flow.endDate));
  }, [flow.quantity, flow.period, flow.endDate]);

  if (!open) {
    return (
      <div style={{ fontSize: 11, color: "#2563eb", marginTop: 2 }}>
        Cadence: <strong>{flow.cadenceLabel || `${flow.quantity}× per ${flow.period}`}</strong>
        {flow.slots && flow.slots.length > 0 && (
          <span style={{ color: "#6b7280" }}> · split: {flow.slots.join(" · ")}</span>
        )}
        {flow.endDate && <span style={{ color: "#6b7280" }}> · until {fmtDate(flow.endDate)}</span>}
        <button className="ap-link" onClick={() => setOpen(true)} style={{ marginLeft: 8 }}>edit</button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", fontSize: 11 }}>
      <input type="number" min={1} value={qty} disabled={busy}
        onChange={(e) => setQty(e.target.value)} style={{ width: 56 }} className="form-control form-control-sm" />
      <span>×&nbsp;per</span>
      <select value={period} disabled={busy} onChange={(e) => setPeriod(e.target.value)} className="form-control form-control-sm" style={{ width: 90 }}>
        {(periods || ["day", "week", "month"]).map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <span>end</span>
      <input type="date" value={endDate} disabled={busy} onChange={(e) => setEndDate(e.target.value)} className="form-control form-control-sm" style={{ width: 140 }} />
      <button className="ap-btn ap-btn-primary" disabled={busy}
        onClick={() => onSave({ quantity: Number(qty) || 1, period, endDate: endDate || null }).then(() => setOpen(false))}>
        {busy ? "Saving…" : "Save"}
      </button>
      <button className="ap-link" disabled={busy} onClick={() => setOpen(false)}>cancel</button>
    </div>
  );
}

// Explicit-slot cadence OVERRIDE editor (super-admin only). Replaces the
// even-split cadence with N exact slots: a daily override is N time-of-day
// pickers that repeat every day; weekly/monthly overrides are N date+time
// pickers (absolute datetimes). The number of slots == quantity; the slot
// inputs grow/shrink with the quantity field. "Apply override" stays disabled
// until EVERY slot is filled (the ticket's verify-before-accept), and the
// worker then schedules the flow on these exact slots. `buildOverrideSlots` is
// pure + exported so the resize/verify logic is unit-tested.
export function buildOverrideSlots(prev, quantity) {
  const n = Math.max(1, Math.min(Number(quantity) || 1, 400));
  const out = (prev || []).slice(0, n);
  while (out.length < n) out.push("");
  return out;
}
// Every slot must be a non-empty string for the override to be acceptable.
export function overrideSlotsComplete(slots) {
  return Array.isArray(slots) && slots.length > 0 && slots.every((s) => s != null && String(s).trim() !== "");
}

function OverrideEditor({ flow, periods, busy, onSave, onClear }) {
  const [open, setOpen] = useState(false);
  const initPeriod = (flow.override && flow.override.period) || flow.period || "day";
  const initSlots = (flow.override && flow.override.slots) || [];
  const [period, setPeriod] = useState(initPeriod);
  const [slots, setSlots] = useState(initSlots.length ? initSlots : buildOverrideSlots([], flow.quantity || 1));

  useEffect(() => {
    if (!open) {
      const p = (flow.override && flow.override.period) || flow.period || "day";
      const s = (flow.override && flow.override.slots) || [];
      setPeriod(p);
      setSlots(s.length ? s : buildOverrideSlots([], flow.quantity || 1));
    }
  }, [flow.override, flow.period, flow.quantity, open]);

  const isDaily = period === "day";
  const complete = overrideSlotsComplete(slots);
  const setQty = (q) => setSlots((prev) => buildOverrideSlots(prev, q));
  const setSlot = (i, v) => setSlots((prev) => prev.map((s, idx) => (idx === i ? v : s)));

  if (!open) {
    return (
      <div style={{ fontSize: 11, marginTop: 2 }}>
        {flow.override ? (
          <span style={{ color: "#7c3aed" }}>
            ⏱ Override: <strong>{flow.overrideLabel || `${flow.override.slots.length} slots / ${flow.override.period}`}</strong>
            {flow.overrideSlotLabels && flow.overrideSlotLabels.length > 0 && (
              <span style={{ color: "#6b7280" }}> · {flow.overrideSlotLabels.join(" · ")}</span>
            )}
            <button className="ap-link" onClick={() => setOpen(true)} style={{ marginLeft: 8 }}>edit slots</button>
          </span>
        ) : (
          <button className="ap-link" style={{ color: "#7c3aed" }} onClick={() => setOpen(true)}>+ Override cadence with exact slots…</button>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 6, padding: 8, background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 8, fontSize: 11 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 8 }}>
        <strong style={{ color: "#7c3aed" }}>Override schedule</strong>
        <span>per</span>
        <select value={period} disabled={busy} onChange={(e) => setPeriod(e.target.value)} className="form-control form-control-sm" style={{ width: 90 }}>
          {(periods || ["day", "week", "month"]).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <span>quantity</span>
        <input type="number" min={1} value={slots.length} disabled={busy}
          onChange={(e) => setQty(e.target.value)} style={{ width: 56 }} className="form-control form-control-sm" />
        <span style={{ color: "#6b7280" }}>
          {isDaily ? "time-of-day slots, repeat every day" : "absolute date + time slots"}
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
        {slots.map((s, i) => (
          <label key={i} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ color: "#6b7280" }}>Slot {i + 1}</span>
            <input
              type={isDaily ? "time" : "datetime-local"}
              value={s || ""}
              disabled={busy}
              onChange={(e) => setSlot(i, e.target.value)}
              className="form-control form-control-sm"
              style={{ width: isDaily ? 110 : 190, borderColor: (s && String(s).trim()) ? "#d1d5db" : "#f59e0b" }}
            />
          </label>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button className="ap-btn ap-btn-primary" disabled={busy || !complete}
          title={complete ? "Apply these exact slots" : "Fill every slot before applying"}
          onClick={() => onSave({ period, slots }).then((ok) => { if (ok) setOpen(false); })}>
          {busy ? "Saving…" : "Apply override"}
        </button>
        {flow.override && (
          <button className="ap-btn ap-btn-danger" disabled={busy}
            onClick={() => onClear().then((ok) => { if (ok) setOpen(false); })}>
            Clear override
          </button>
        )}
        <button className="ap-link" disabled={busy} onClick={() => setOpen(false)}>cancel</button>
        {!complete && <span style={{ color: "#b45309" }}>Fill all {slots.length} slots to apply.</span>}
      </div>
    </div>
  );
}

// "Make automatic for future brands" form for one non-automatic flow.
function MakeAutomatic({ flow, periods, companies, brands, busy, onCreate }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(flow.defaultCadence ? flow.defaultCadence.quantity : 1);
  const [period, setPeriod] = useState(flow.defaultCadence ? flow.defaultCadence.period : "day");
  const [companyScope, setCompanyScope] = useState("all");
  const [companyId, setCompanyId] = useState("");
  const [brandScope, setBrandScope] = useState("all");
  const [brandId, setBrandId] = useState("");
  const [endDate, setEndDate] = useState("");

  const brandOptions = useMemo(
    () => (companyScope === "one" && companyId ? brands.filter((b) => b.companyId === companyId) : brands),
    [brands, companyScope, companyId]
  );

  if (!open) {
    return <button className="ap-btn ap-btn-primary" onClick={() => setOpen(true)}>Make automatic…</button>;
  }
  return (
    <div style={{ marginTop: 6, padding: 8, background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 11 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <strong>Cadence</strong>
        <input type="number" min={1} value={qty} disabled={busy} onChange={(e) => setQty(e.target.value)} style={{ width: 56 }} className="form-control form-control-sm" />
        <span>× per</span>
        <select value={period} disabled={busy} onChange={(e) => setPeriod(e.target.value)} className="form-control form-control-sm" style={{ width: 90 }}>
          {(periods || ["day", "week", "month"]).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <span>end</span>
        <input type="date" value={endDate} disabled={busy} onChange={(e) => setEndDate(e.target.value)} className="form-control form-control-sm" style={{ width: 140 }} />
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <strong>Companies</strong>
        <select value={companyScope} disabled={busy} onChange={(e) => { setCompanyScope(e.target.value); setCompanyId(""); }} className="form-control form-control-sm" style={{ width: 130 }}>
          <option value="all">All companies</option>
          <option value="one">One company</option>
        </select>
        {companyScope === "one" && (
          <select value={companyId} disabled={busy} onChange={(e) => { setCompanyId(e.target.value); setBrandId(""); }} className="form-control form-control-sm" style={{ width: 200 }}>
            <option value="">— select company —</option>
            {companies.map((c) => <option key={c.companyId} value={c.companyId}>{c.label}</option>)}
          </select>
        )}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <strong>Brands</strong>
        <select value={brandScope} disabled={busy} onChange={(e) => { setBrandScope(e.target.value); setBrandId(""); }} className="form-control form-control-sm" style={{ width: 130 }}>
          <option value="all">All brands</option>
          <option value="one">One brand</option>
        </select>
        {brandScope === "one" && (
          <select value={brandId} disabled={busy} onChange={(e) => setBrandId(e.target.value)} className="form-control form-control-sm" style={{ width: 200 }}>
            <option value="">— select brand —</option>
            {brandOptions.map((b) => <option key={b.brandId} value={b.brandId}>{b.brandName}</option>)}
          </select>
        )}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="ap-btn ap-btn-primary" disabled={busy}
          onClick={() => onCreate({
            flowKey: flow.flowKey,
            quantity: Number(qty) || 1, period, endDate: endDate || null,
            companyScope, companyId: companyScope === "one" ? companyId : "",
            brandScope, brandId: brandScope === "one" ? brandId : "",
          }).then((ok) => { if (ok) setOpen(false); })}>
          {busy ? "Saving…" : "Create policy"}
        </button>
        <button className="ap-link" disabled={busy} onClick={() => setOpen(false)}>cancel</button>
      </div>
    </div>
  );
}

// ── Sub-tabs ────────────────────────────────────────────────────────────────
// The "All Pipelines" view is split into mutually-exclusive sub-tabs so the
// operator sees one slice at a time (only one renders, default "Standing"):
//   Standing  — per-brand flows that run continuously (kind === "standing").
//   Recurring — per-brand live recurrences (kind === "recurrence", still active).
//   Pipelined — the non-automatic registry flows + "make automatic" policies.
//   Finished  — per-brand recurrences that have reached a terminal status.
//   Archived  — shelved brands whose automation is halted (mirrors the
//               Self-Construction archive shelf). ALWAYS LAST — after "Finished".
const TABS = [
  { key: "standing", label: "Standing" },
  { key: "recurring", label: "Recurring" },
  { key: "pipelined", label: "Pipelined" },
  { key: "finished", label: "Finished" },
  { key: "archived", label: "Archived" },
];

// A recurrence is "finished" once its FlowRun reaches a terminal status.
const TERMINAL_STATUSES = ["completed", "failed", "cancelled"];

const PREVIEW_STATUS_COLORS = {
  posted: "#059669", approved: "#2563eb", scheduled: "#7c3aed",
  "pending-content-approval": "#d97706", archived: "#9ca3af", draft: "#6b7280", other: "#9ca3af",
};
const prettyStatus = (s) => String(s || "").replace("pending-content-approval", "pending approval");

// Collapsible "preview by date" for a brand's flow — mirrors the Run-Flows
// artifact preview. Lazy-loads /pipelines/preview on first expand and groups the
// generated artifacts (posts) by date, each date expandable to its items.
function FlowPreview({ brand, flow }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dates, setDates] = useState(null);
  const [openDates, setOpenDates] = useState({});
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await get(`/api/admin/flows/pipelines/preview?brandId=${brand.brandId}&flowKey=${flow.registryFlowKey}&days=21`);
      setDates((r && r.dates) || []);
    } catch (e) { setErr((e && (e.error || e.message)) || "Preview failed"); }
    finally { setLoading(false); }
  }, [brand.brandId, flow.registryFlowKey]);

  const onToggle = () => { const next = !open; setOpen(next); if (next && dates === null) load(); };

  return (
    <div style={{ marginTop: 6 }}>
      <button className="ap-link" onClick={onToggle} style={{ fontSize: 11, color: "#7c3aed" }}>
        {open ? "▾" : "▸"} Preview by date
      </button>
      {open && (
        <div style={{ marginTop: 4, borderLeft: "2px solid #ede9fe", paddingLeft: 8 }}>
          {loading && <div style={{ fontSize: 11, color: "#888" }}>loading…</div>}
          {err && <div style={{ fontSize: 11, color: "#dc2626" }}>{err}</div>}
          {dates && dates.length === 0 && <div style={{ fontSize: 11, color: "#888" }}>No artifacts in the last 21 days.</div>}
          {dates && dates.map((d) => (
            <div key={d.date} style={{ marginBottom: 3 }}>
              <button className="ap-link" onClick={() => setOpenDates((p) => ({ ...p, [d.date]: !p[d.date] }))}
                style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>
                {openDates[d.date] ? "▾" : "▸"} {d.date} · {d.count} post{d.count !== 1 ? "s" : ""}
                {Object.entries(d.counts || {}).map(([k, n]) => (
                  <span key={k} className="ap-chip" style={{ background: PREVIEW_STATUS_COLORS[k] || "#e5e7eb", color: "#fff", marginLeft: 4 }}>
                    {n} {prettyStatus(k)}
                  </span>
                ))}
              </button>
              {openDates[d.date] && (
                <div style={{ paddingLeft: 14, marginTop: 2 }}>
                  {d.items.map((it) => (
                    <div key={it.id} style={{ fontSize: 11, color: "#374151", display: "flex", gap: 8, padding: "1px 0" }}>
                      <span style={{ color: "#6b7280", minWidth: 42 }}>{it.scheduledAt ? new Date(it.scheduledAt).toISOString().slice(11, 16) : "—"}</span>
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</span>
                      <span style={{ color: PREVIEW_STATUS_COLORS[it.postedOk ? "posted" : it.queueStatus] || "#6b7280", flexShrink: 0 }}>
                        {it.postedOk ? `posted ×${it.postedOk}` : prettyStatus(it.queueStatus)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Which sub-tab a per-brand flow belongs to. Standing flows live in "standing";
// terminal recurrences in "finished"; every other (live) recurrence in
// "recurring". Pipelined flows are not per-brand and are handled separately.
export function flowTab(f) {
  if (!f) return "standing";
  if (f.kind === "standing") return "standing";
  if (f.kind === "recurrence" && TERMINAL_STATUSES.includes(f.status)) return "finished";
  return "recurring";
}

// Narrow a per-brand list to just the flows that belong to `tab`, dropping any
// brand left with no matching flow so each tab only lists relevant brands.
// Archived brands are SHELVED: they're excluded from every working tab and
// surface only under "archived" (where every archived brand is listed, flows
// kept intact so the operator can see what's been halted).
export function brandsForTab(brands, tab) {
  if (tab === "archived") return (brands || []).filter((b) => b && b.archived);
  return (brands || [])
    .filter((b) => !(b && b.archived))
    .map((b) => ({ ...b, flows: (b.flows || []).filter((f) => flowTab(f) === tab) }))
    .filter((b) => b.flows.length > 0);
}

// The per-brand archive control, mirroring the Self-Construction archive button.
// An archived brand's control un-archives it (restores its automation); any
// other brand's control archives it (shelves it + halts its automation). Pure +
// exported for tests so the move-target + label can't silently drift.
export function brandArchiveControl(b) {
  const archived = !!(b && b.archived);
  return archived
    ? { archived: true, label: "♻ Un-archive", target: false, title: "Un-archive — restore this brand and its automation" }
    : { archived: false, label: "📦 Archive", target: true, title: "Archive this brand — shelve it and halt all its automation" };
}

// The per-brand collapse control. A collapsed card hides ALL its flows (and
// their steps + buttons) so the brand is just a single header row; expanding it
// shows all the existing data again. Pure + exported for tests so the chevron
// and a11y label can't silently drift. `collapsed` is the brand's current state.
export function brandCollapseControl(collapsed) {
  return collapsed
    ? { collapsed: true, icon: "▸", label: "Expand", title: "Expand — show all flows, steps and controls for this brand" }
    : { collapsed: false, icon: "▾", label: "Collapse", title: "Collapse — hide all flows and show this brand on one row" };
}

export default function AllPipelines() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [msg, setMsg] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("standing"); // one sub-tab at a time; default "Standing"
  const [collapsed, setCollapsed] = useState({}); // brandId -> true when its card is collapsed (flows hidden)

  // Flip one brand card between collapsed (single header row) and expanded.
  const toggleCollapse = useCallback((brandId) => {
    setCollapsed((m) => ({ ...m, [brandId]: !m[brandId] }));
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await get("/api/admin/flows/pipelines");
      setData(r && r.success ? r : (r && r.brands ? r : null));
      setErr(null);
    } catch (e) { setErr((e && (e.error || e.message)) || "Failed to load pipelines"); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, [load]);

  const toggle = useCallback(async (brand, flow, enabled) => {
    const key = `${brand.brandId}:${flow.runId || flow.flowKey}`;
    setBusyKey(key); setMsg(null); setErr(null);
    try {
      await post("/api/admin/flows/pipelines/toggle", {
        brandId: brand.brandId, scope: flow.kind, flowKey: flow.flowKey, runId: flow.runId, enabled,
      }, false);
      setMsg(`${flow.label} ${enabled ? "enabled" : "disabled"} for ${brand.brandName}.`);
      await load();
    } catch (e) {
      setErr((e && (e.error || e.message)) || "Toggle failed");
    } finally { setBusyKey(null); }
  }, [load]);

  // Flip a flow's auto-PUBLISH switch (BrandSettings.autopilot.<feature>) inline.
  // Writes the brand's settings + re-arms the subscription if it's on the engine.
  const setAutopilot = useCallback(async (brand, flow, on) => {
    const key = `ap:${brand.brandId}:${flow.flowKey}`;
    setBusyKey(key); setMsg(null); setErr(null);
    try {
      await post("/api/admin/flows/pipelines/autopilot", {
        brandId: brand.brandId, flowKey: flow.flowKey, enabled: on,
      }, false);
      setMsg(`${AUTOPILOT_LABEL[flow.autopilotFeature] || "Auto-publish"} ${on ? "on" : "off"} for ${brand.brandName}.`);
      await load();
    } catch (e) {
      setErr((e && (e.error || e.message)) || "Auto-publish toggle failed");
    } finally { setBusyKey(null); }
  }, [load]);

  const archiveBrand = useCallback(async (brand, archived) => {
    setBusyKey(`arch:${brand.brandId}`); setMsg(null); setErr(null);
    try {
      await post("/api/admin/flows/pipelines/archive", { brandId: brand.brandId, archived }, false);
      setMsg(`${brand.brandName} ${archived ? "archived — its automation is halted" : "un-archived — its automation is restored"}.`);
      await load();
    } catch (e) {
      setErr((e && (e.error || e.message)) || "Archive failed");
    } finally { setBusyKey(null); }
  }, [load]);

  const saveCadence = useCallback(async (brand, flow, cadence) => {
    const key = `cad:${brand.brandId}:${flow.flowKey}`;
    setBusyKey(key); setMsg(null); setErr(null);
    try {
      await post("/api/admin/flows/pipelines/cadence", {
        brandId: brand.brandId, flowKey: flow.flowKey, ...cadence,
      }, false);
      setMsg(`Cadence updated for ${flow.label} on ${brand.brandName}.`);
      await load();
    } catch (e) {
      setErr((e && (e.error || e.message)) || "Cadence update failed");
    } finally { setBusyKey(null); }
  }, [load]);

  // Phase-2: move a standing flow onto the unified recurrence engine (subscribe)
  // or back to its legacy bespoke scheduler (unsubscribe). Same flow, different
  // trigger — the only difference between this and a manual Run-Flow.
  const toggleSubscription = useCallback(async (brand, flow) => {
    const key = `sub:${brand.brandId}:${flow.registryFlowKey}`;
    setBusyKey(key); setMsg(null); setErr(null);
    try {
      if (flow.subscribed) {
        await post("/api/admin/flows/pipelines/unsubscribe", { brandId: brand.brandId, flowKey: flow.registryFlowKey }, false);
        setMsg(`${flow.label} moved back to the legacy scheduler for ${brand.brandName}.`);
      } else {
        await post("/api/admin/flows/pipelines/subscribe", { brandId: brand.brandId, flowKey: flow.registryFlowKey, quantity: flow.quantity || 1, period: flow.period || "day" }, false);
        setMsg(`${flow.label} now runs on the unified engine for ${brand.brandName}.`);
      }
      await load();
    } catch (e) {
      setErr((e && (e.error || e.message)) || "Subscription change failed");
    } finally { setBusyKey(null); }
  }, [load]);

  const saveOverride = useCallback(async (brand, flow, body) => {
    const key = `ovr:${brand.brandId}:${flow.flowKey}`;
    setBusyKey(key); setMsg(null); setErr(null);
    try {
      await post("/api/admin/flows/pipelines/cadence-override", {
        brandId: brand.brandId, flowKey: flow.flowKey, ...body,
      }, false);
      setMsg(`Schedule override applied for ${flow.label} on ${brand.brandName}.`);
      await load();
      return true;
    } catch (e) {
      setErr((e && (e.error || e.message)) || "Could not apply override");
      return false;
    } finally { setBusyKey(null); }
  }, [load]);

  const clearOverride = useCallback(async (brand, flow) => {
    const key = `ovr:${brand.brandId}:${flow.flowKey}`;
    setBusyKey(key); setMsg(null); setErr(null);
    try {
      await post("/api/admin/flows/pipelines/cadence-override", {
        brandId: brand.brandId, flowKey: flow.flowKey, clear: true,
      }, false);
      setMsg(`Schedule override cleared for ${flow.label} on ${brand.brandName}.`);
      await load();
      return true;
    } catch (e) {
      setErr((e && (e.error || e.message)) || "Could not clear override");
      return false;
    } finally { setBusyKey(null); }
  }, [load]);

  const createPolicy = useCallback(async (body) => {
    setBusyKey(`policy:${body.flowKey}`); setMsg(null); setErr(null);
    try {
      await post("/api/admin/flows/pipelines/automation-defaults", body, false);
      setMsg(`Policy created — ${body.flowKey} is now automatic for matching future brands.`);
      await load();
      return true;
    } catch (e) {
      setErr((e && (e.error || e.message)) || "Could not create policy");
      return false;
    } finally { setBusyKey(null); }
  }, [load]);

  const deletePolicy = useCallback(async (id) => {
    setBusyKey(`del:${id}`); setMsg(null); setErr(null);
    try {
      await remove(`/api/admin/flows/pipelines/automation-defaults/${id}`, {});
      setMsg("Policy removed.");
      await load();
    } catch (e) {
      setErr((e && (e.error || e.message)) || "Could not remove policy");
    } finally { setBusyKey(null); }
  }, [load]);

  const brands = useMemo(() => {
    const list = (data && data.brands) || [];
    const needle = q.trim().toLowerCase();
    return needle ? list.filter((b) => (b.brandName || "").toLowerCase().includes(needle)) : list;
  }, [data, q]);

  // Company options for the policy scope selector, derived from the brand list.
  const companies = useMemo(() => {
    const all = (data && data.brands) || [];
    const seen = new Map();
    for (const b of all) {
      if (!b.companyId) continue;
      if (!seen.has(b.companyId)) seen.set(b.companyId, b.brandName);
    }
    return [...seen.entries()].map(([companyId, firstBrand]) => ({
      companyId, label: `Company ${companyId.slice(-6)} (e.g. ${firstBrand})`,
    }));
  }, [data]);
  const brandOptionsAll = useMemo(
    () => ((data && data.brands) || []).map((b) => ({ brandId: b.brandId, brandName: b.brandName, companyId: b.companyId })),
    [data]
  );

  // Per-tab brand slices, computed off the q-filtered brand list so the brand
  // filter applies within every tab.
  const standingBrands = useMemo(() => brandsForTab(brands, "standing"), [brands]);
  const recurringBrands = useMemo(() => brandsForTab(brands, "recurring"), [brands]);
  const finishedBrands = useMemo(() => brandsForTab(brands, "finished"), [brands]);
  const archivedBrands = useMemo(() => brandsForTab(brands, "archived"), [brands]);

  if (err && !data) return <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>;
  if (!data) return <div style={{ fontSize: 12, color: "#999" }}>Loading all pipelines…</div>;

  const periods = data.periods || ["day", "week", "month"];
  const otherFlows = data.otherFlows || [];
  const policies = data.automationDefaults || [];

  // One brand card with its (already tab-filtered) flows. Shared by the
  // Standing / Recurring / Finished tabs — each passes a different flow slice.
  const renderBrandCard = (b) => {
    const isCollapsed = !!collapsed[b.brandId];
    const cc = brandCollapseControl(isCollapsed);
    return (
    <div className="ap-brand" key={b.brandId}>
      <div className="ap-brand-h" style={isCollapsed ? { borderBottom: "none", borderRadius: 10 } : undefined}>
        <span style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
          <button
            className="ap-collapse"
            aria-expanded={!isCollapsed}
            onClick={() => toggleCollapse(b.brandId)}
            title={cc.title}
          >
            {cc.icon}
          </button>
          {b.brandName}
          {b.disabled && <span className="ap-chip" style={{ background: "#b91c1c", marginLeft: 8 }}>brand halted</span>}
          {b.archived && <span className="ap-chip" style={{ background: "#7c3aed", marginLeft: 8 }}>archived</span>}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, color: "#888" }}>{b.flows.length} flow(s)</span>
          {(() => {
            const a = brandArchiveControl(b);
            return (
              <button
                className="ap-btn"
                disabled={busyKey === `arch:${b.brandId}`}
                onClick={() => archiveBrand(b, a.target)}
                title={a.title}
                style={{ color: a.archived ? "#7c3aed" : "#6b7280", borderColor: a.archived ? "#c4b5fd" : "#d1d5db" }}
              >
                {busyKey === `arch:${b.brandId}` ? "…" : a.label}
              </button>
            );
          })()}
        </span>
      </div>
      {!isCollapsed && b.flows.map((f) => {
        const key = `${b.brandId}:${f.runId || f.flowKey}`;
        const terminal = f.kind === "recurrence" && TERMINAL_STATUSES.includes(f.status);
        return (
          <div className="ap-flow" key={key}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>
                {f.label}
                <span className="ap-kind" style={{ background: f.kind === "standing" ? "#eef2ff" : "#ecfeff", color: f.kind === "standing" ? "#4338ca" : "#0e7490" }}>
                  {f.kind === "standing" ? "standing" : "recurrence"}
                </span>
                {terminal && <span className="ap-chip" style={{ background: "#9ca3af", marginLeft: 6 }}>{f.status}</span>}
              </div>
              {f.kind === "standing" && f.cadenceEditable ? (
                <CadenceEditor flow={f} periods={periods} busy={busyKey === `cad:${b.brandId}:${f.flowKey}`}
                  onSave={(cad) => saveCadence(b, f, cad)} />
              ) : (
                <div style={{ fontSize: 11, color: "#2563eb", marginTop: 2 }}>
                  {f.kind === "standing" ? f.cadence : recurrenceProjection(f)}
                </div>
              )}
              {/* Super-admin explicit-slot override — offered only for the
                  override-capable standing flows (posting / FAQ / engagement
                  analysis), whose worker scheduler actually fires on the exact
                  slots. Per-contact CRM + stale-driven enrichment can't honor
                  explicit wall-clock slots, so they show a short note instead of
                  a no-op editor. */}
              {f.kind === "standing" && (f.overrideCapable ? (
                <OverrideEditor flow={f} periods={periods} busy={busyKey === `ovr:${b.brandId}:${f.flowKey}`}
                  onSave={(body) => saveOverride(b, f, body)} onClear={() => clearOverride(b, f)} />
              ) : (
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                  Explicit-slot override not available for this flow (runs on its own trigger).
                </div>
              ))}
              <div style={{ marginTop: 4 }}>
                {(f.steps || []).map((s) => (
                  <span key={s.key} className="ap-chip" title={s.kind || ""}>
                    {s.label}{s.count ? ` (${s.count})` : ""}
                  </span>
                ))}
              </div>
              {/* Per-date artifact preview (collapsible) for flows with dated
                  output — currently the social flow's posts. */}
              {f.subscribable && f.registryFlowKey && <FlowPreview brand={b} flow={f} />}
            </div>
            <div style={{ flexShrink: 0, textAlign: "right" }}>
              {/* Phase-2: move this standing flow onto the unified recurrence
                  engine (same engine that runs manual Run-Flows) or back to its
                  legacy bespoke scheduler. Only for flows that map to a registry
                  flow with a real recurrence (social / faq). */}
              {f.kind === "standing" && f.subscribable && !b.archived && (
                <div style={{ marginBottom: 6 }}>
                  <button
                    disabled={busyKey === `sub:${b.brandId}:${f.registryFlowKey}` || b.disabled}
                    onClick={() => toggleSubscription(b, f)}
                    title={f.subscribed
                      ? "Running on the unified recurrence engine — click to move back to the legacy scheduler"
                      : "Move this flow onto the unified recurrence engine (the same engine as manual Run-Flows)"}
                    style={{
                      background: f.subscribed ? "#7c3aed" : "#fff",
                      color: f.subscribed ? "#fff" : "#7c3aed",
                      border: "1px solid #7c3aed", borderRadius: 6, fontSize: 11, padding: "2px 8px", cursor: "pointer",
                    }}>
                    {busyKey === `sub:${b.brandId}:${f.registryFlowKey}` ? "…" : f.subscribed ? "⚡ Unified engine" : "Subscribe →"}
                  </button>
                </div>
              )}
              {/* Inline auto-PUBLISH toggle, pulled straight from the brand's
                  BrandSettings.autopilot.<feature>. On = this flow's output is
                  published automatically; off = held for approval. Flipping it
                  writes the brand's settings and re-arms the subscription. */}
              {f.kind === "standing" && f.autopilotFeature && !terminal && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end", marginBottom: 6 }}
                  title={`Auto-publish (BrandSettings.autopilot.${f.autopilotFeature}). On → publish automatically; off → hold for approval.`}>
                  <span style={{ fontSize: 10, color: f.autopilotOn ? "#059669" : "#9ca3af" }}>
                    {AUTOPILOT_LABEL[f.autopilotFeature] || "auto-publish"}
                  </span>
                  <Toggle enabled={!!f.autopilotOn} busy={busyKey === `ap:${b.brandId}:${f.flowKey}` || b.disabled || b.archived}
                    onChange={(on) => setAutopilot(b, f, on)} />
                </div>
              )}
              {terminal
                ? <span style={{ fontSize: 11, color: "#9ca3af" }}>finished</span>
                : <Toggle enabled={f.enabled} busy={busyKey === key || b.disabled || b.archived} onChange={(on) => toggle(b, f, on)} />}
            </div>
          </div>
        );
      })}
    </div>
    );
  };

  // The brand-filter input + per-brand cards for a single per-brand tab.
  const renderBrandList = (list, emptyMsg) => (
    <>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <input className="form-control" placeholder="Filter brands…" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 260 }} />
        <span style={{ fontSize: 11, color: "#888" }}>{list.length} brand(s)</span>
      </div>
      {list.length === 0 && <div style={{ fontSize: 12, color: "#999" }}>{emptyMsg}</div>}
      {list.map((b) => renderBrandCard(b))}
    </>
  );

  return (
    <div style={{ fontSize: 13 }}>
      <style>{`
        .ap-toggle{border:none;border-radius:999px;padding:3px 12px;font-size:11px;font-weight:600;cursor:pointer}
        .ap-btn{border:1px solid #d1d5db;border-radius:6px;padding:3px 10px;font-size:11px;font-weight:600;cursor:pointer;background:#fff}
        .ap-btn-primary{background:#2563eb;color:#fff;border-color:#2563eb}
        .ap-btn-danger{background:#fff;color:#b91c1c;border-color:#fca5a5}
        .ap-link{border:none;background:none;color:#2563eb;font-size:11px;cursor:pointer;padding:0;text-decoration:underline}
        .ap-collapse{border:1px solid #d1d5db;background:#fff;border-radius:6px;width:22px;height:22px;line-height:1;font-size:11px;color:#374151;cursor:pointer;padding:0;display:inline-flex;align-items:center;justify-content:center}
        .ap-collapse:hover{background:#f3f4f6}
        .ap-brand{border:1px solid #e5e7eb;border-radius:10px;margin-bottom:12px;background:#fff}
        .ap-brand-h{display:flex;justify-content:space-between;align-items:center;padding:8px 12px;background:#fafbfc;border-bottom:1px solid #eef0f2;border-radius:10px 10px 0 0}
        .ap-flow{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;padding:8px 12px;border-bottom:1px solid #f4f4f4}
        .ap-flow:last-child{border-bottom:none}
        .ap-chip{display:inline-block;background:#6b7280;color:#fff;border-radius:999px;padding:1px 7px;font-size:10px;margin:1px 3px 1px 0}
        .ap-kind{font-size:9px;text-transform:uppercase;letter-spacing:.5px;border-radius:4px;padding:1px 5px;margin-left:6px}
        .ap-section{margin-top:22px}
        .ap-section h4{font-size:14px;font-weight:700;margin-bottom:4px}
        .ap-policy{display:flex;justify-content:space-between;align-items:center;gap:10px;padding:6px 10px;border-bottom:1px solid #f1f5f9;font-size:12px}
        .ap-tabs{display:flex;gap:4;border-bottom:1px solid #e5e5e5;margin-bottom:16px}
        .ap-tab{padding:8px 14px;background:none;border:none;border-bottom:2px solid transparent;font-size:13px;cursor:pointer;color:#15172b}
        .ap-tab.active{border-bottom-color:#15172b;font-weight:600}
      `}</style>

      {/* Four mutually-exclusive sub-tabs — only one renders, default "Standing". */}
      <div className="ap-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? "ap-tab active" : "ap-tab"}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {err && <div className="alert alert-warning" style={{ fontSize: 12 }}>{err}</div>}
      {msg && <div className="alert alert-success" style={{ fontSize: 12 }}>{msg}</div>}

      {/* ── Standing: flows that start automatically on brand creation ── */}
      {tab === "standing" && (
        <div role="tabpanel">
          <h4 style={{ fontSize: 15, fontWeight: 700 }}>Default automated pipelines</h4>
          <p className="text-muted" style={{ fontSize: 13 }}>
            The flows that start <strong>automatically when a brand is created</strong> — idea generation &amp;
            auto-posting and brand enrichment — listed per brand with their cadence and end date. Change the
            idea/auto-posting cadence (e.g. <em>3× per day</em>) and the system splits posting equally over the
            period (early / mid / end of day). Toggle a flow off for one brand without halting the whole brand.
          </p>
          {renderBrandList(standingBrands, "No brands have standing flows.")}
        </div>
      )}

      {/* ── Recurring: live per-brand recurrences (FlowRuns still active) ── */}
      {tab === "recurring" && (
        <div role="tabpanel">
          <h4 style={{ fontSize: 15, fontWeight: 700 }}>Recurring flows</h4>
          <p className="text-muted" style={{ fontSize: 13 }}>
            Per-brand <strong>recurrences</strong> that re-run each period — projected to the next run and end
            date. Pause or resume any one for a single brand without halting the brand.
          </p>
          {renderBrandList(recurringBrands, "No live recurrences.")}
        </div>
      )}

      {/* ── Pipelined: non-automatic registry flows + make-automatic policies ── */}
      {tab === "pipelined" && (
        <div role="tabpanel">
          <h4 style={{ fontSize: 15, fontWeight: 700 }}>Other flows — not automatic by default</h4>
          <p className="text-muted" style={{ fontSize: 13 }}>
            Fully-pipelined flows that don't start on brand creation. Make any of them automatic for
            <strong> future brands</strong> at a chosen cadence — scoped to all companies or one, and all brands or one.
          </p>
          {otherFlows.map((f) => (
            <div className="ap-brand" key={f.flowKey} style={{ padding: "8px 12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{f.label} <span className="ap-kind" style={{ background: "#fef3c7", color: "#92400e" }}>pipelined</span></div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{f.desc}</div>
                  <div style={{ marginTop: 4 }}>
                    {(f.steps || []).map((s) => <span key={s.key} className="ap-chip">{s.label}</span>)}
                  </div>
                </div>
                <div style={{ flexShrink: 0 }}>
                  <MakeAutomatic flow={f} periods={periods} companies={companies} brands={brandOptionsAll}
                    busy={busyKey === `policy:${f.flowKey}`} onCreate={createPolicy} />
                </div>
              </div>
            </div>
          ))}
          {otherFlows.length === 0 && <div style={{ fontSize: 12, color: "#999" }}>No additional flows.</div>}

          {/* Active policies */}
          <div className="ap-section">
            <h4>Active "make automatic" policies ({policies.length})</h4>
            {policies.length === 0 && <div style={{ fontSize: 12, color: "#999" }}>No policies yet — future brands use the built-in defaults.</div>}
            {policies.length > 0 && (
              <div className="ap-brand">
                {policies.map((p) => (
                  <div className="ap-policy" key={p.id}>
                    <div>
                      <strong>{p.flowLabel}</strong>
                      <span className="ap-chip" style={{ marginLeft: 6 }}>{p.quantity}× / {p.period}</span>
                      {!p.enabled && <span className="ap-chip" style={{ background: "#9ca3af" }}>disabled</span>}
                      <span style={{ color: "#6b7280", marginLeft: 6 }}>
                        {p.companyScope === "all" ? "all companies" : `company ${String(p.companyId).slice(-6)}`}
                        {" · "}
                        {p.brandScope === "all" ? "all brands" : `brand ${String(p.brandId).slice(-6)}`}
                        {p.endDate ? ` · until ${fmtDate(p.endDate)}` : ""}
                      </span>
                    </div>
                    <button className="ap-btn ap-btn-danger" disabled={busyKey === `del:${p.id}`} onClick={() => deletePolicy(p.id)}>
                      {busyKey === `del:${p.id}` ? "…" : "Remove"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Finished: per-brand recurrences that reached a terminal status ── */}
      {tab === "finished" && (
        <div role="tabpanel">
          <h4 style={{ fontSize: 15, fontWeight: 700 }}>Finished flows</h4>
          <p className="text-muted" style={{ fontSize: 13 }}>
            Per-brand recurrences that have <strong>completed, failed, or been cancelled</strong>. Kept here for
            reference — they no longer run.
          </p>
          {renderBrandList(finishedBrands, "No finished recurrences yet.")}
        </div>
      )}

      {/* ── Archived: shelved brands whose automation is halted (LAST tab) ── */}
      {tab === "archived" && (
        <div role="tabpanel">
          <h4 style={{ fontSize: 15, fontWeight: 700 }}>Archived brands</h4>
          <p className="text-muted" style={{ fontSize: 13 }}>
            Brands shelved here are <strong>archived</strong> — like the Self-Construction archive, all of their
            automation is halted (no schedulers, recurrences, AI generation, or posting run) and they're out of the
            way of the working tabs. Un-archive a brand to restore it and its automation.
          </p>
          {renderBrandList(archivedBrands, "No archived brands — use the Archive button on a brand to shelve it.")}
        </div>
      )}
    </div>
  );
}
